import { Check, Minus } from "lucide-react";

export const ACCENT = { demo: "#94A3B8", basic: "#2AB7CA", standard: "#FF5A79", premium: "#9B5DE5" };

export function planPrice(plan, cycle) {
  if (!plan || plan.price === 0) return { label: "Free", suffix: "", note: "" };
  if (cycle === "yearly" && plan.price_yearly) {
    const perMo = (plan.price_yearly / 12).toFixed(2);
    return { label: `$${plan.price_yearly}`, suffix: "/yr", note: `$${perMo}/mo · billed yearly` };
  }
  return { label: `$${plan.price}`, suffix: "/mo", note: "" };
}

export function BillingToggle({ cycle, setCycle, className = "" }) {
  return (
    <div className={`inline-flex items-center rounded-full bg-slate-100 p-1 ${className}`} data-testid="billing-toggle">
      <button
        type="button"
        data-testid="billing-monthly"
        onClick={() => setCycle("monthly")}
        className={`rounded-full px-5 py-2 text-sm font-bold transition-colors ${cycle === "monthly" ? "bg-white text-slate-900 shadow" : "text-slate-500 hover:text-slate-700"}`}
      >
        Monthly
      </button>
      <button
        type="button"
        data-testid="billing-yearly"
        onClick={() => setCycle("yearly")}
        className={`flex items-center gap-2 rounded-full px-5 py-2 text-sm font-bold transition-colors ${cycle === "yearly" ? "bg-white text-slate-900 shadow" : "text-slate-500 hover:text-slate-700"}`}
      >
        Yearly
        <span className="rounded-full bg-emerald-100 text-emerald-700 px-2 py-0.5 text-[11px] font-bold">Save 25%</span>
      </button>
    </div>
  );
}

const YesNo = ({ v, color }) =>
  v ? <Check className="h-4 w-4 mx-auto" style={{ color }} /> : <Minus className="h-4 w-4 mx-auto text-slate-300" />;

export function PlanComparison({ plans, cycle = "monthly" }) {
  if (!plans?.length) return null;
  const rows = [
    { key: "price", label: "Price" },
    { key: "quality", label: "Streaming quality" },
    { key: "devices", label: "Watch on" },
    { key: "premium_shows", label: "Premium shows", bool: true },
    { key: "games", label: "Learning games" },
    { key: "downloads", label: "Offline downloads", bool: true },
    { key: "trial", label: "Free trial" },
  ];
  return (
    <div className="overflow-x-auto rounded-3xl border-2 border-[#f1e9d9] bg-white" data-testid="plan-comparison">
      <table className="w-full min-w-[640px] text-left">
        <thead>
          <tr className="border-b border-slate-100">
            <th className="p-4 text-sm font-bold text-slate-500">Compare plans</th>
            {plans.map((p) => (
              <th key={p.id} className="p-4 text-center">
                <div className="font-display text-lg font-bold" style={{ color: ACCENT[p.id] }}>{p.name}</div>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.key} className="border-b border-slate-50 last:border-0">
              <td className="p-4 text-sm font-semibold text-slate-600">{r.label}</td>
              {plans.map((p) => {
                const accent = ACCENT[p.id];
                let content;
                if (r.key === "price") {
                  const pr = planPrice(p, cycle);
                  content = <span className="font-bold text-slate-900">{pr.label}<span className="text-xs text-slate-400 font-normal">{pr.suffix}</span></span>;
                } else if (r.key === "trial") {
                  const t = p.compare?.trial;
                  content = t ? <span className="text-sm font-semibold text-emerald-600">{t}</span> : <Minus className="h-4 w-4 mx-auto text-slate-300" />;
                } else if (r.bool) {
                  content = <YesNo v={p.compare?.[r.key]} color={accent} />;
                } else {
                  content = <span className="text-sm text-slate-600">{p.compare?.[r.key]}</span>;
                }
                return <td key={p.id} className="p-4 text-center">{content}</td>;
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
